import { queryKeys } from "@l2a/shared";
import { Building2, Contact2, GitMerge, PauseCircle, Users } from "lucide-react";
import { defineFeature } from "../types";

/**
 * Clients — client accounts, the companies behind them and the people you deal
 * with (PRD roadmap item 2).
 *
 * VOCABULARY, which every string in this area follows (PRD §4.1):
 *   Company  — a business you sell to.
 *   Contact  — a person at one or more companies.
 *   Client   — the account relationship with a company after the first win.
 * Invoices, contracts, engagements and portal access hang off the CLIENT.
 *
 * The three canonical routes here are fixed by `ENTITY_ROUTES` in @l2a/shared:
 * the API puts /clients/:id, /companies/:id and /contacts/:id into search
 * results, notifications and e-mails, so this feature must serve them.
 */
export default defineFeature({
  key: "clients",
  title: "Clients",
  entities: ["client", "company", "contact"],
  routes: [
    { path: "clients", lazy: () => import("./page") },
    { path: "clients/:id", lazy: () => import("./detail") },
    { path: "companies", lazy: () => import("./companies") },
    { path: "companies/:id", lazy: () => import("./company-detail") },
    { path: "contacts", lazy: () => import("./contacts") },
    { path: "contacts/:id", lazy: () => import("./contact-detail") },
  ],
  nav: [
    {
      key: "clients",
      label: "Clients",
      to: "/clients",
      icon: Users,
      group: "clients",
      order: 10,
      permission: "clients:read",
      description: "Accounts, what they are worth and what happens next",
      badgeQueryKey: queryKeys.list("client", { status: ["active"] }),
    },
    {
      key: "companies",
      label: "Companies",
      to: "/companies",
      icon: Building2,
      group: "clients",
      order: 20,
      permission: "clients:read",
      description: "Every business you deal with, won or not",
    },
    {
      key: "contacts",
      label: "Contacts",
      to: "/contacts",
      icon: Contact2,
      group: "clients",
      order: 30,
      permission: "clients:read",
      description: "The people behind the accounts",
    },
  ],
  quickCreate: [
    {
      key: "client",
      label: "Client",
      icon: Users,
      to: "/clients?new=1",
      permission: "clients:write",
      order: 20,
      description: "Turn a company into an account you run",
    },
    {
      key: "company",
      label: "Company",
      icon: Building2,
      to: "/companies?new=1",
      permission: "clients:write",
      order: 21,
      description: "A business you sell to",
    },
    {
      key: "contact",
      label: "Contact",
      icon: Contact2,
      to: "/contacts?new=1",
      permission: "clients:write",
      order: 22,
      description: "A person at a company",
    },
  ],
  commands: [
    {
      key: "clients.active",
      label: "Show active clients",
      icon: Users,
      to: "/clients?status=active",
      permission: "clients:read",
      group: "actions",
      keywords: ["accounts", "live", "running", "book of business"],
    },
    {
      key: "clients.paused",
      label: "Show paused clients",
      icon: PauseCircle,
      to: "/clients?status=paused",
      permission: "clients:read",
      group: "actions",
      keywords: ["on hold", "frozen", "resume"],
      description: "Accounts whose retainer billing is on hold",
    },
    {
      key: "companies.prospects",
      label: "Companies that are not clients yet",
      icon: Building2,
      to: "/companies?hasClient=no",
      permission: "clients:read",
      group: "actions",
      keywords: ["prospects", "pipeline", "unconverted"],
    },
    {
      key: "contacts.whatsapp",
      label: "Contacts with WhatsApp consent",
      icon: Contact2,
      to: "/contacts?whatsappOptIn=yes",
      permission: "clients:read",
      group: "actions",
      keywords: ["opt in", "dpdp", "broadcast", "consent"],
    },
    {
      key: "companies.merge",
      label: "Merge duplicate companies",
      icon: GitMerge,
      to: "/companies",
      permission: "clients:delete",
      group: "actions",
      keywords: ["duplicate", "dedupe", "combine"],
      description: "Open a company, then merge the duplicate into it",
    },
  ],
});
